/**
 * OnePieceWidget Component
 * Special dashboard widget for One Piece - The GOAT
 * Shows current progress, arc info, and quick actions
 * NOW with IN-APP playback using embeddable sources!
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getSmartWatchlist } from '../../services/storage';
import { getArcByEpisode, getSagaByEpisode, getProgressPercentage } from '../../data/onePieceArcs';
import { ANIME_EXTERNAL_SOURCES } from '../../services/streaming';
import type { SmartWatchlistItem } from '../../types';

// One Piece TMDB ID
const ONE_PIECE_TMDB = 37854;

// Convert episode number to TMDB season/episode
// One Piece on TMDB is split into multiple seasons
function getSeasonEpisode(absoluteEp: number): { season: number; episode: number } {
  // TMDB season breakdown for One Piece (approximate)
  const seasonBreaks = [
    { season: 1, start: 1, end: 61 },      // East Blue
    { season: 2, start: 62, end: 77 },     // Alabasta Saga P1
    { season: 3, start: 78, end: 92 },     // Drum Island
    { season: 4, start: 93, end: 130 },    // Alabasta
    { season: 5, start: 131, end: 143 },   // Post-Alabasta
    { season: 6, start: 144, end: 195 },   // Skypiea
    { season: 7, start: 196, end: 228 },   // G-8 + Davy Back
    { season: 8, start: 229, end: 263 },   // Water 7
    { season: 9, start: 264, end: 336 },   // Enies Lobby
    { season: 10, start: 337, end: 381 },  // Thriller Bark
    { season: 11, start: 382, end: 429 },  // Sabaody
    { season: 12, start: 430, end: 456 },  // Impel Down
    { season: 13, start: 457, end: 516 },  // Marineford
    { season: 14, start: 517, end: 578 },  // Fish-Man Island
    { season: 15, start: 579, end: 628 },  // Punk Hazard
    { season: 16, start: 629, end: 746 },  // Dressrosa
    { season: 17, start: 747, end: 782 },  // Zou
    { season: 18, start: 783, end: 877 },  // Whole Cake Island
    { season: 19, start: 878, end: 891 },  // Reverie
    { season: 20, start: 892, end: 1085 }, // Wano
    { season: 21, start: 1086, end: 1122 }, // Egghead (ongoing)
  ];

  for (const s of seasonBreaks) {
    if (absoluteEp >= s.start && absoluteEp <= s.end) {
      return { season: s.season, episode: absoluteEp - s.start + 1 };
    }
  }

  // Default to last season for episodes beyond known range
  return { season: 21, episode: absoluteEp - 1085 };
}

export default function OnePieceWidget() {
  const navigate = useNavigate();
  const [onePiece, setOnePiece] = useState<SmartWatchlistItem | null>(null);
  const [playMode, setPlayMode] = useState<'inapp' | 'hianime'>('inapp');
  const [showSourcePicker, setShowSourcePicker] = useState(false);

  useEffect(() => {
    const watchlist = getSmartWatchlist();
    const op = watchlist.find(item => item.title === 'One Piece' || item.tmdbId === 37854);
    setOnePiece(op || null);
  }, []);

  if (!onePiece) {
    return null;
  }

  const currentEpisode = onePiece.progress?.currentEpisode || 1;
  const totalEpisodes = onePiece.progress?.totalEpisodes || 1122;
  const arc = getArcByEpisode(currentEpisode);
  const saga = getSagaByEpisode(currentEpisode);
  const progress = getProgressPercentage(currentEpisode);

  return (
    <div className="one-piece-widget relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-900/40 via-black to-yellow-900/30 border border-yellow-500/30">
      {/* Straw Hat Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-4 right-4 text-8xl">🏴‍☠️</div>
      </div>

      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🏴‍☠️</span>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-red-500 bg-clip-text text-transparent">
                ONE PIECE
              </h2>
              <p className="text-xs text-yellow-500/70 font-medium">THE GOAT • King of Anime</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-bold">
            👑 PRIORITY #1
          </span>
        </div>

        {/* Current Progress */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-black/30 rounded-lg p-3">
            <p className="text-xs text-gray-400 mb-1">Current Episode</p>
            <p className="text-2xl font-bold text-white">{currentEpisode}</p>
            <p className="text-xs text-gray-500">of {totalEpisodes}</p>
          </div>
          <div className="bg-black/30 rounded-lg p-3">
            <p className="text-xs text-gray-400 mb-1">Current Arc</p>
            <p className="text-lg font-bold text-yellow-400">{arc?.name || 'Unknown'}</p>
            <p className="text-xs text-gray-500">{saga} Saga</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-gray-400">Journey Progress</span>
            <span className="text-yellow-400 font-medium">{progress}%</span>
          </div>
          <div className="h-2 bg-black/50 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-yellow-500 to-red-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {totalEpisodes - currentEpisode} episodes to catch up
          </p>
        </div>

        {/* Key Events from Current Arc */}
        {arc && arc.keyEvents.length > 0 && (
          <div className="mb-4 p-3 bg-black/30 rounded-lg">
            <p className="text-xs text-gray-400 mb-2">Arc Highlights</p>
            <div className="flex flex-wrap gap-1">
              {arc.keyEvents.map((event, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 bg-yellow-500/10 text-yellow-400/80 rounded text-xs"
                >
                  {event}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex gap-2">
          {playMode === 'inapp' ? (
            // IN-APP playback button - navigates to Watch page
            <button
              onClick={() => {
                const { season, episode } = getSeasonEpisode(currentEpisode);
                navigate(`/watch/tv/${ONE_PIECE_TMDB}?s=${season}&e=${episode}`);
              }}
              className="flex-1 py-3 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 rounded-lg font-semibold text-sm text-center transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-500/20"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
              </svg>
              Watch Ep {currentEpisode}
            </button>
          ) : (
            // HiAnime external link
            <a
              href={ANIME_EXTERNAL_SOURCES.hianime.watch('one-piece-100', currentEpisode)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg font-semibold text-sm text-center transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              HiAnime Ep {currentEpisode}
            </a>
          )}
          <Link
            to="/watchlist"
            className="px-4 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-semibold text-sm transition-colors"
          >
            📋
          </Link>
        </div>

        {/* Play Mode Toggle */}
        <div className="mt-4 relative">
          <button
            onClick={() => setShowSourcePicker(!showSourcePicker)}
            className="w-full flex items-center justify-center gap-2 text-xs text-gray-400 hover:text-gray-200 transition-colors py-2"
          >
            <span>Play mode:</span>
            <span className={`px-2 py-0.5 rounded font-medium flex items-center gap-1 ${
              playMode === 'inapp' ? 'bg-green-500/20 text-green-400' : 'bg-purple-500/20 text-purple-400'
            }`}>
              {playMode === 'inapp' ? '🎬 In-App' : '🌐 HiAnime'}
              <svg className={`w-3 h-3 transition-transform ${showSourcePicker ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>

          {/* Mode Dropdown */}
          {showSourcePicker && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-gray-900 rounded-lg border border-white/10 shadow-xl overflow-hidden z-10 min-w-[200px]">
              <button
                onClick={() => {
                  setPlayMode('inapp');
                  setShowSourcePicker(false);
                }}
                className={`w-full px-4 py-3 text-sm text-left flex items-center gap-3 hover:bg-white/10 transition-colors ${
                  playMode === 'inapp' ? 'bg-green-500/20' : ''
                }`}
              >
                <span className="text-lg">🎬</span>
                <div>
                  <p className={`font-medium ${playMode === 'inapp' ? 'text-green-400' : 'text-gray-200'}`}>In-App Player</p>
                  <p className="text-xs text-gray-500">Watch without leaving</p>
                </div>
                {playMode === 'inapp' && (
                  <svg className="w-4 h-4 ml-auto text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => {
                  setPlayMode('hianime');
                  setShowSourcePicker(false);
                }}
                className={`w-full px-4 py-3 text-sm text-left flex items-center gap-3 hover:bg-white/10 transition-colors ${
                  playMode === 'hianime' ? 'bg-purple-500/20' : ''
                }`}
              >
                <span className="text-lg">🌐</span>
                <div>
                  <p className={`font-medium ${playMode === 'hianime' ? 'text-purple-400' : 'text-gray-200'}`}>HiAnime</p>
                  <p className="text-xs text-gray-500">Open in new tab</p>
                </div>
                {playMode === 'hianime' && (
                  <svg className="w-4 h-4 ml-auto text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
